import { useCallback, useState, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, CollectionFilesyncAPI, useSendRequestMutation, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType,
  DriveWatchedErrorMessages, EntryItemBase, COLLECTION_PROTOCOL_VERSIONS, useGetGrants, useGetPermissions, PermissionSetType, GrantSetType, EntryItem, CollectionEntity, DriveEntryType,
  useGetCollectionInfo, DriveErrorMessages, CollectionInfoMap, NodeCollectionInfo, MaintainerGrantsMap
} from "src/common";
import { uiActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

interface WatchDataType extends EntryItemBase {
    collection_protocol_version: COLLECTION_PROTOCOL_VERSIONS;
    watch: boolean;
}

export function useBulkWatch(current_account: Account) {
    const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
    const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
    const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
    const { grants, getGrants, error: grant_error, destroyGrants } = useGetGrants(current_account);
    const [error, setError] = useState<boolean>(false);
    const [bulk_action_errors, setBulkActionErrors] = useState<string[]>([]);
    const [success, setSuccess] = useState<boolean>(false);
    const [sendRequest] = useSendRequestMutation();
    const { collection_info, collection_info_error, getCollectionInfo, resetCollectionInfo } = useGetCollectionInfo(current_account);
    const dispatch = useAppDispatch();
    const bulk_action_errors_ref = useRef<string[]>([]);
    const node_queue_ref = useRef<EntryItem[]>([]);
    const working_node_ref = useRef<EntryItem>();
    const node_collection_info_ref = useRef<CollectionInfoMap>(new Map<string, NodeCollectionInfo>);
    function setCollectionInfoRef(id: string, node_collection_info: NodeCollectionInfo) {
        node_collection_info_ref.current.set(id, node_collection_info);
    }

    const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
    function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
        maintainer_grants_ref.current.set(id, maintainer_grants);
    }

    const data_ref = useRef<WatchDataType | undefined>();
    function setData(_data?: WatchDataType) {
        data_ref.current = _data;
    }

    const permissions_ref = useRef(permissions);
    function setPermissionsRef(_permissions?: PermissionSetType) {
        permissions_ref.current = _permissions;
    }

    // Description: Handle collection Info for bulk watch
    useEffect(() => {
        if (!!collection_info) {
            // NOTE: Save to node_collection_info_ref map
            (!!permissions_ref.current && permissions_ref.current.collection_id.B64() === collection_info.collection_id) &&
                setCollectionInfoRef(collection_info.collection_id, { collection_info, permissions: permissions_ref.current });

            !!working_node_ref.current ? handleBulkWatchItem(working_node_ref.current, collection_info) :
                handleItemError(`${collection_info.collection_name}_${collection_info.id}}`, DriveWatchedErrorMessages.default, { stack_error: { data: collection_info }, stack_message: DriveErrorMessages.error_fetching_collection_information });
        }

        !!collection_info_error &&
            handleItemError(working_node_ref.current?.name || "name not available", DriveWatchedErrorMessages.default, { stack_error: { data: working_node_ref.current }, stack_message: DriveErrorMessages.error_fetching_collection_information });
    }, [collection_info, collection_info_error]);

    // Description: Handle the name field in the watch node list
    useEffect(() => {
        if (!!permissions && !!data_ref.current) {
            setPermissionsRef(permissions);
            destroyPermissions(); // NOTE: Return the permission state to undefined to be able to detect future changes
            handleWatchNodePermissions(data_ref.current, permissions);
        }
    }, [permissions]);

    // Description: useEffect hook for getting grant per row
    useEffect(() => {
        if (!!grants) {
            // NOTE: Save to grants map
            setMaintainerGrantsRef(grants.id, grants);
            (!!data_ref.current && !!permissions_ref.current) &&
                handleWatchNode(data_ref.current, permissions_ref.current, grants);
        }


        if (!!grant_error) {
            const item_name = data_ref.current?.name || working_node_ref.current?.name || "name not available";
            handleItemError(item_name, DriveWatchedErrorMessages.default, { stack_error: { data: working_node_ref.current }, stack_message: DriveErrorMessages.error_fetching_grant });
        };
    }, [grants, grant_error]);


    // Description: Handle the collection V1 and V2 paths and grants/permissions
    async function handleWatchNodePermissions(_data: WatchDataType, _permissions: PermissionSetType) {
        if (_data.collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V2) {
            if (!!_data.maintainer_id) {
                // NOTE: Do a check for cached assets to bypass getting same collection again
                const _maintainer_grants = maintainer_grants_ref.current.get(_data.maintainer_id);
                !!_maintainer_grants ? handleWatchNode(_data, _permissions, _maintainer_grants) :
                    getGrants(_data.maintainer_id, _data.collection_id, default_permissions, default_grants);
            } else {
                handleItemError(working_node_ref.current?.name || _data.name || "name not available", DriveWatchedErrorMessages.default, { stack_error: { data: working_node_ref.current }, stack_message: DriveWatchedErrorMessages.error_watch_bulk_maintainer_id });
            }
        } else {
            handleWatchNode(_data, _permissions);
        }
    }

    // Description: Set Node and descendants to watch OR unwatch depending on the watch flag
    async function handleWatchNode(_data: WatchDataType, _permissions: PermissionSetType, _grants?: GrantSetType) {
        const id = _data.id;
        const drive_request = await CollectionFilesyncAPI.setWatchNode(current_account, _data.collection_id, id, _permissions, _grants);
        async function callback(message: FSMessage) {
            (message.getStatus() === FSStatus.OK) ?
                getNext() :
                handleItemError(_data.name || _data.id, DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_watch_bulk_anchor }, message);
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_setting_node_watch);
    }

    // Description: Assemble the correct data for bulk actions
    function handleBulkWatchItem(_entry: EntryItem, _collection_info: CollectionEntity, _permissions?: PermissionSetType) {
        resetCollectionInfo();
        const collection_id = _collection_info.collection_id;
        const collection_protocol_version = _collection_info.collection_protocol_version;
        const maintainer_id = _collection_info.maintainer_id;
        const id = _entry.type !== DriveEntryType.LINK ? _entry.id :
            (collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V2 && !!maintainer_id) ? maintainer_id :
                (collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V1 && _collection_info.is_root && !!_collection_info.root_id) ? _collection_info.root_id :
                    _collection_info.id;

        const new_data_ref = {
            id,
            maintainer_id,
            collection_id,
            linked_collection_id: _entry.linked_collection_id,
            type: _entry.type,
            collection_protocol_version,
            watch: true
        };

        setData(new_data_ref);
        // NOTE: Use pre-cached permissions
        !!_permissions ? handleWatchNodePermissions(new_data_ref, _permissions) : getPermissions(collection_id, default_permissions);
    }

    // Description: Move to next item in queue
    function getNext() {
        working_node_ref.current = node_queue_ref.current.shift();
        if (!!working_node_ref.current) {
            // NOTE: Do a check for cached assets to bypass getting same collection again
            const node_collection_info = node_collection_info_ref.current.get(working_node_ref.current.collection_id);
            !!node_collection_info ?
                handleBulkWatchItem(working_node_ref.current, node_collection_info.collection_info, node_collection_info.permissions) :
                getCollectionInfo(working_node_ref.current);
        } else {
            handleSuccess();
        }
    }

    // Description:
    function handleSuccess() {
        setBulkActionErrors(bulk_action_errors_ref.current);
        setSuccess(true);
    }

    // Description: reset item on error and move to next
    function handleItemErrorReset(item_name: string) {
        bulk_action_errors_ref.current.push(item_name);
        working_node_ref.current = undefined;
        destroyGrants(); // NOTE: destroy grants to allow for a next iteration
        setData();
        getNext();
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveWatchedErrorMessages.error_sending_request) {
        if (!!request) {
            request.setCallback(callback);
            sendRequest(request);
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_error, stack_message: DriveWatchedErrorMessages.error_sending_request });
        }
    }

    // Description:  Handle queue item error
    function handleItemError(item_name: string, message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useBulkWatch Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
        handleItemErrorReset(item_name);
    }

    // Description:  send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useEmailFrequency Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // -----------------------------
    // Callbacks
    // -----------------------------
    // Description: Call hook for watching / unwatching nodes and descendants
    const setBulkWatchState = useCallback((entries: EntryItem[]) => {
        _.forEach(entries, (entry: EntryItem) => node_queue_ref.current.push(entry));
        getNext();
    }, []);

    // Description: Reset all from component
    const reset = useCallback(() => {
        setSuccess(false);
        setError(false);
        setData(undefined);
        setPermissionsRef(undefined);
        setBulkActionErrors([]);
        destroyPermissions();
        destroyGrants();
        resetCollectionInfo();
        bulk_action_errors_ref.current = [];
        working_node_ref.current = undefined;
        node_queue_ref.current = [];
    }, []);

    return {
        setBulkWatchState,
        reset,
        success,
        error,
        bulk_action_errors
    };
}
